import PageShell, { PageHeader } from "@/components/PageShell";
import Seo from "@/components/Seo";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Plus } from "lucide-react";

const faqs = [
  { q: "Which countries' live TV is included today?", a: "The Desi TV Package streams live channels from India, Bangladesh, Nepal and Pakistan. A dedicated Arabic TV Package is coming soon." },
  { q: "What devices does Nema TV work on?", a: "Roku, Fire TV, Android TV, Android phones and tablets, iOS iPhone and iPad, and any modern web browser. Sign in once, watch anywhere." },
  { q: "Can I record or rewind live TV?", a: "Yes. Selected channels support a 3-day DVR — pause, rewind or catch up on anything that aired in the last 72 hours." },
  { q: "Is there a contract or cancellation fee?", a: "Never. Every plan can be paused or cancelled from your account in under a minute. You keep access until the end of your billing period." },
  { q: "How is billing handled?", a: "Choose 1, 3, 6 or 12 months. Longer plans lower your effective monthly cost. Once you submit the signup form we'll email you a secure checkout link." },
  { q: "Do you offer a free trial?", a: "We regularly run promotional offers. Drop your details in the signup form and we'll share any active trial or discount code with you." },
  { q: "Can I share my account?", a: "Your subscription is for household use. Simultaneous streams may be limited on the same account, depending on your plan." },
  { q: "How is streaming quality?", a: "We stream in HD wherever the source feed supports it, with adaptive bitrate that adjusts to your connection." },
  { q: "How do I cancel?", a: "Sign in, go to Account, and hit Cancel. Access continues until the end of the paid period." },
  { q: "What if a channel isn't working?", a: "Contact support@nematv.com with the channel name, region and the time you noticed the issue — we usually restore feeds within minutes." },
];

const Item = ({ f, i, open, setOpen }) => {
  const isOpen = open === i;
  return (
    <div className="border-b border-white/10">
      <button
        data-testid={`faq-item-${i}`}
        onClick={() => setOpen(isOpen ? -1 : i)}
        className="w-full flex items-center justify-between text-left py-6 group"
      >
        <span className="font-display text-lg md:text-2xl text-white font-semibold pr-6">{f.q}</span>
        <motion.span
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.3 }}
          className="shrink-0 w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 group-hover:border-[#22D3EE]/60 group-hover:text-[#22D3EE] transition-colors"
        >
          <Plus className="w-4 h-4" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="pb-6 pr-16 text-white/65 max-w-3xl leading-relaxed">{f.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FaqPage = () => {
  const [open, setOpen] = useState(0);
  return (
    <PageShell>
      <Seo
        title="FAQ — Nema TV"
        description="Answers to the most common questions about plans, devices, DVR and billing on Nema TV."
        path="/faq"
        schemas={[{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map(f => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }]}
      />
      <PageHeader eyebrow="Frequently asked" title="Questions, answered." subtitle="Everything you need to know about plans, playback and devices." />
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        {faqs.map((f, i) => <Item key={f.q} f={f} i={i} open={open} setOpen={setOpen} />)}
      </div>
    </PageShell>
  );
};

export default FaqPage;
